import { Text } from '@wix/design-system';
import { useTranslation } from '@wix/wix-i18n-config';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useStore } from 'react-redux';

import { TLocalesKeys } from '../../../../types';
import { logChatbotDisplayWarning } from '../../../bi';
import { IBotTyping } from '../../../common/messageCreators';
import { MessageBubble } from '../MessageBubble';
import { MessageItem } from '../MessageItem';
import s from './BotTypingClassic.scss';

const WARNING_TIMEOUT = 5; // seconds
const EXTRA_WARNING_TIMEOUT = 10; // seconds

export const BotTypingClassic: FC<IBotTyping> = ({ id }) => {
  const store = useStore();
  const [showMessageWarning, setShowMessageWarning] = useState(false);
  const [showMessageExtraWarning, setShowMessageExtraWarning] = useState(false);
  const { t } = useTranslation();
  const messageWarning = useMemo(() => {
    if (showMessageExtraWarning) {
      return t<TLocalesKeys>('bot_response_takes_too_long_extra_warning');
    }
    if (showMessageWarning) {
      return t<TLocalesKeys>('bot_response_takes_too_long');
    }
  }, [t, showMessageWarning, showMessageExtraWarning]);

  const logBiEvent = useCallback(
    (timeWaited: number, messageLocalKey: TLocalesKeys) => {
      logChatbotDisplayWarning(store.getState(), {
        timeWaited,
        messageText: t<TLocalesKeys>(messageLocalKey),
        messageId: id,
      });
    },
    [t, id, store],
  );
  useEffect(() => {
    const warningTimer = setTimeout(() => {
      setShowMessageWarning(true);
      logBiEvent(WARNING_TIMEOUT, 'bot_response_takes_too_long');
    }, WARNING_TIMEOUT * 1000);
    const extraWarningTimer = setTimeout(() => {
      setShowMessageExtraWarning(true);
      logBiEvent(EXTRA_WARNING_TIMEOUT, 'bot_response_takes_too_long_extra_warning');
    }, EXTRA_WARNING_TIMEOUT * 1000);
    return () => {
      clearTimeout(warningTimer);
      clearTimeout(extraWarningTimer);
    };
  }, [logBiEvent]);

  return (
    <>
      <MessageItem id={id} dataHook="typing">
        <MessageBubble isFirstInGroup>
          <span className={s.dots}>
            <span className={s.dot1} />
            <span className={s.dot2} />
            <span className={s.dot3} />
          </span>
        </MessageBubble>
      </MessageItem>
      {messageWarning ? (
        // Using extra <Item /> as it provides styles for positioning and fade-in effect
        <MessageItem id={`${id}-warning`} dataHook="typing-warning">
          <Text skin="disabled" size="small">
            {messageWarning}
          </Text>
        </MessageItem>
      ) : null}
    </>
  );
};
